import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, FileText, CheckCircle, LogOut, ChevronDown } from 'lucide-react';
import api from '../services/api';
import ReactMarkdown from 'react-markdown';

const ConsentAcceptance = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [missingConsents, setMissingConsents] = useState([]);
  const [documents, setDocuments] = useState({});
  const [checkedConsents, setCheckedConsents] = useState({});
  const [activeTab, setActiveTab] = useState(null);
  const [scrolledToBottom, setScrolledToBottom] = useState({});
  const scrollRef = useRef(null);

  useEffect(() => {
    loadConsentStatus();
  }, []);

  const loadConsentStatus = async () => {
    try {
      const [statusRes, privacyRes, termsRes] = await Promise.all([
        api.get('/lgpd/consent-status'),
        api.get('/lgpd/privacy-policy'),
        api.get('/lgpd/terms-of-use')
      ]);

      const missing = statusRes.data.data.missingConsents || [];
      setMissingConsents(missing);

      if (missing.length === 0) {
        navigate('/dashboard');
        return;
      }

      setDocuments({
        privacy_policy: privacyRes.data.data,
        terms_of_use: termsRes.data.data
      });

      setActiveTab(missing[0]?.type || 'terms_of_use');
    } catch (err) {
      setError('Erro ao carregar documentos. Tente novamente.');
      console.error('Error loading consent status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      setScrolledToBottom(prev => ({ ...prev, [activeTab]: true }));
    }
  };

  const handleCheck = (consentType) => {
    setCheckedConsents(prev => ({
      ...prev,
      [consentType]: !prev[consentType]
    }));
  };

  const allRequiredChecked = missingConsents
    .filter(c => ['terms_of_use', 'privacy_policy'].includes(c.type))
    .every(c => checkedConsents[c.type]);

  const handleAccept = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const consentsToAccept = Object.entries(checkedConsents)
        .filter(([_, checked]) => checked)
        .map(([type]) => {
          const doc = documents[type];
          return { consentType: type, version: doc?.version || '1.0' };
        });

      await api.post('/lgpd/accept-consents', { consents: consentsToAccept });
      navigate('/dashboard');
    } catch (err) {
      setError('Erro ao registrar consentimento. Tente novamente.');
      console.error('Error accepting consents:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-brand-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src="/brand/gia-logo-horizontal.svg" alt="GiA - Guia Inteligente do Autismo" className="h-12 w-auto" />
          <button
            onClick={handleDecline}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
          >
            <LogOut className="h-4 w-4" />
            Sair
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Title Card */}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-brand-100 rounded-lg">
              <Shield className="h-6 w-6 text-brand-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Termos e Privacidade</h2>
              <p className="text-sm text-gray-600">
                Para continuar utilizando o sistema, e necessario aceitar os documentos abaixo.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm">
            {error}
          </div>
        )}

        {/* Document Tabs */}
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden mb-6">
          <div className="flex border-b">
            {missingConsents.map(consent => (
              <button
                key={consent.type}
                onClick={() => setActiveTab(consent.type)}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === consent.type
                    ? 'text-brand-600 border-b-2 border-brand-600 bg-brand-50/50'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <FileText className="h-4 w-4" />
                  {consent.title}
                </div>
              </button>
            ))}
          </div>

          {/* Document Content */}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="p-6 max-h-96 overflow-y-auto prose prose-sm max-w-none"
          >
            <ReactMarkdown>{documents[activeTab]?.content || 'Carregando...'}</ReactMarkdown>
          </div>

          {!scrolledToBottom[activeTab] && (
            <div className="px-6 py-2 bg-gray-50 border-t flex items-center justify-center gap-1 text-xs text-gray-500">
              <ChevronDown className="h-3 w-3 animate-bounce" />
              Role para baixo para ler o documento completo
            </div>
          )}
        </div>

        {/* Consent Checkboxes */}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6 space-y-4">
          {missingConsents.map(consent => (
            <label
              key={consent.type}
              className="flex items-start gap-3 cursor-pointer group"
            >
              <input
                type="checkbox"
                checked={checkedConsents[consent.type] || false}
                onChange={() => handleCheck(consent.type)}
                className="mt-1 h-4 w-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900 group-hover:text-brand-600">
                  Li e aceito {consent.title} (v{consent.version})
                </span>
                {['terms_of_use', 'privacy_policy'].includes(consent.type) && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                    Obrigatorio
                  </span>
                )}
              </div>
            </label>
          ))}

          {/* Optional consents */}
          <div className="border-t pt-4 mt-2">
            <p className="text-xs text-gray-500 mb-3 font-medium uppercase tracking-wide">Consentimentos opcionais</p>

            <label className="flex items-start gap-3 cursor-pointer group mb-3">
              <input
                type="checkbox"
                checked={checkedConsents['health_data_processing'] || false}
                onChange={() => handleCheck('health_data_processing')}
                className="mt-1 h-4 w-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900 group-hover:text-brand-600">
                  Autorizo o tratamento de dados de saude para fins de acompanhamento clinico
                </span>
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                  Opcional
                </span>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer group mb-3">
              <input
                type="checkbox"
                checked={checkedConsents['ai_data_processing'] || false}
                onChange={() => handleCheck('ai_data_processing')}
                className="mt-1 h-4 w-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900 group-hover:text-brand-600">
                  Autorizo o uso de IA para analise de dados clinicos anonimizados
                </span>
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                  Opcional
                </span>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={checkedConsents['marketing'] || false}
                onChange={() => handleCheck('marketing')}
                className="mt-1 h-4 w-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900 group-hover:text-brand-600">
                  Autorizo o recebimento de comunicacoes e materiais informativos
                </span>
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                  Opcional
                </span>
              </div>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            onClick={handleDecline}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Recusar e sair
          </button>
          <button
            onClick={handleAccept}
            disabled={!allRequiredChecked || submitting}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              allRequiredChecked && !submitting
                ? 'bg-brand-700 text-white hover:bg-brand-800 shadow-sm'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Registrando...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4" />
                Aceitar e Continuar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsentAcceptance;
